import { DEFAULT_STATSIG_PARTYTOWN_DYNAMIC_CONFIG } from '@src/experiments/statsig-integration/statsig-dynamic-config.constants'
import type { PartytownDynamicConfig } from '@src/server-side/api/api.types'

// Shared Partytown constants + helpers.
//
// Partytown relocates resource-intensive third-party scripts (here: Meta's
// fbevents.js) off the main thread into a web worker. Scripts tagged with
// `type="text/partytown"` are picked up by the Partytown runtime; the
// `ptupdate` event asks it to re-scan the DOM for scripts injected after boot.

export const PARTYTOWN_SCRIPT_TYPE = 'text/partytown'

export const PARTYTOWN_UPDATE_EVENT = 'ptupdate'

// Partytown's service worker. The loader registers it itself, but we also
// pre-register it from an inline script in _document <head> so that on cold
// first visits the SW is active well before the loader creates the sandbox
// iframe. `partytown-sandbox-sw.html` is a VIRTUAL url synthesized by this
// SW's fetch handler — if the iframe request races SW activation and reaches
// the network it 404s and the sandbox never boots (silent pixel loss).
export const PARTYTOWN_SW_URL = '/~partytown/partytown-sw.js'
export const PARTYTOWN_SW_SCOPE = '/~partytown/'

// How long Partytown waits for the sandbox before giving up and replaying `text/partytown` scripts on
// the main thread now comes from the `ssr-partytown` dynamic config (`fallbackTimeoutMs`, default 5000
// — Partytown's own default is ~10s, lowered so a document can't sit eventless that long). NOTE: that
// built-in fallback only replays scripts present at DOMContentLoaded — FbPixel's bootstrap is injected
// later, so FbPixel runs its own watchdog sequenced AFTER this timeout, derived from the SAME resolved
// config (see armPartytownPixelWatchdog in FbPixel.ts).

// Bounds for the Statsig-controlled timeouts (`ssr-partytown`). A console typo must not be able to
// park every document on a 10-minute sandbox wait (silent pixel loss) or fire the watchdog instantly.
export const PARTYTOWN_TIMEOUT_MIN_MS = 1000
export const PARTYTOWN_TIMEOUT_MAX_MS = 15000

// `forward` entries are serialized into the inline loader snippet, so they must look like plain
// global accessors (`fbq`, `ttq`, `dataLayer.push`). Anything else is dropped rather than emitted.
const PARTYTOWN_FORWARD_IDENTIFIER = /^[A-Za-z_$][\w$.]*$/

const clampTimeout = (value: unknown, fallback: number): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }
  return Math.min(Math.max(value, PARTYTOWN_TIMEOUT_MIN_MS), PARTYTOWN_TIMEOUT_MAX_MS)
}

/**
 * Normalises the Statsig-resolved `ssr-partytown` config into values safe to serialize into the
 * inline loader snippet and the worker bootstrap. Falls back per key to the shipped defaults, so a
 * missing config (or a missing key on an older config revision) reproduces current behaviour exactly.
 *
 * Isomorphic on purpose: `_document` resolves it on the server from pageProps, FbPixel resolves it in
 * the browser from seeded memory, and both must agree — the client watchdog is only meaningful if it
 * is derived from the same `fallbackTimeoutMs` the server emitted.
 */
export function resolvePartytownConfig(config: Partial<PartytownDynamicConfig> | null | undefined): {
  fbPixelAutoConfig: boolean
  fallbackTimeoutMs: number
  pixelWatchdogGraceMs: number
  forward: string[]
} {
  const forward = Array.isArray(config?.forward)
    ? config.forward.filter(
        (entry): entry is string => typeof entry === 'string' && PARTYTOWN_FORWARD_IDENTIFIER.test(entry),
      )
    : []

  return {
    fbPixelAutoConfig:
      typeof config?.fbPixelAutoConfig === 'boolean'
        ? config.fbPixelAutoConfig
        : DEFAULT_STATSIG_PARTYTOWN_DYNAMIC_CONFIG.fbPixelAutoConfig,
    fallbackTimeoutMs: clampTimeout(
      config?.fallbackTimeoutMs,
      DEFAULT_STATSIG_PARTYTOWN_DYNAMIC_CONFIG.fallbackTimeoutMs,
    ),
    pixelWatchdogGraceMs: clampTimeout(
      config?.pixelWatchdogGraceMs,
      DEFAULT_STATSIG_PARTYTOWN_DYNAMIC_CONFIG.pixelWatchdogGraceMs,
    ),
    forward: forward.length > 0 ? forward : [...DEFAULT_STATSIG_PARTYTOWN_DYNAMIC_CONFIG.forward],
  }
}

/**
 * Coerces the Partytown experiment treatment membership (from `pageProps.isPartytownEnabled`,
 * resolved per user by the `all__fb-pixel_partytown-offload__web` experiment) to a strict boolean.
 * Only an explicit `true` (treatment bucket) opts a request into the worker path.
 */
export function isPartytownEnabled(enabled: boolean | null | undefined): boolean {
  return enabled === true
}

/**
 * Tells the Partytown runtime to re-scan the DOM for `text/partytown` scripts.
 * Required after injecting such a script post-boot (Partytown only scans at
 * startup otherwise). No-op on the server.
 */
export function dispatchPartytownUpdate(): void {
  if (typeof window === 'undefined') {
    return
  }

  window.dispatchEvent(new CustomEvent(PARTYTOWN_UPDATE_EVENT))
}
